import { useState, type ComponentType } from 'react';
import { useLocation } from 'react-router-dom';
import Snackbar from './components/Snackbar';
import AuthHeader from './components/AuthHeader';
import LoginForm from './components/forms/LoginForm';
import { useLoginHint } from './auth/loginHint';
import dooglieIcon from './images/login/DOL_Standalone.svg';
import { Box } from '@mui/material';

import './styles/LoginPage.css';

type AuthLayoutProps = { toggleAlertMessage: (message: string) => void };

const withAuthLayout = <P extends object>(
  WrappedComponent: ComponentType<P & AuthLayoutProps>
) => {
  return (props: P) => {
    const location = useLocation();
    // A failed sign-in redirect (AuthCallback) arrives carrying its reason.
    const authError = (location.state as { authError?: string } | null)?.authError;
    const [alertMessage, setAlertMessage] = useState(authError ?? '');
    const [alertMessageVisibility, setAlertMessageVisibility] = useState(Boolean(authError));
    // A sign-in failure can be an instruction ("ask your admin to..."), so it
    // stays up long enough to read. Other alerts keep the snackbar's default.
    const [alertDuration, setAlertDuration] = useState(authError ? 15000 : undefined);

    const toggleAlertMessage = (message: string) => {
      setAlertMessage(message);
      setAlertDuration(undefined);
      setAlertMessageVisibility(true);
    };

    return (
      <Box className="page-container">
        <Box className="login-container">
          {alertMessageVisibility && (
            <Snackbar
              message={alertMessage}
              duration={alertDuration}
              onClose={() => setAlertMessageVisibility(false)}
            />
          )}
          <AuthHeader />
          <Box className="auth-form">
            <Box className="dooglie-icon-wrapper">
              <img
                src={dooglieIcon}
                alt="Dooglie Icon"
                className="dooglie-icon"
              />
            </Box>
            <WrappedComponent
              {...props}
              toggleAlertMessage={toggleAlertMessage}
            />
          </Box>
        </Box>
      </Box>
    );
  };
};

const LoginContent = () => {
  const loginHint = useLoginHint();
  return <LoginForm loginHint={loginHint} />;
};

export const LoginPage = withAuthLayout(LoginContent);
